import React, { useEffect, useState } from 'react';
import { supabase } from '../../supabase/config';
import { Calendar, Clock, Plus, Trash2, X, Building2, ChevronLeft, ChevronRight, Edit2, Check, Save, ScrollText } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useAlert } from '../../context/AlertContext';
import { useTranslation } from 'react-i18next';

const CalendarPage = () => {
    const { t } = useTranslation();
    const [tenants, setTenants] = useState([]);
    const [meetings, setMeetings] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isMeetingModalOpen, setMeetingModalOpen] = useState(false);
    const [curDate, setCurDate] = useState(new Date());
    const [newMeeting, setNewMeeting] = useState({
        tenantId: '',
        customBusinessName: '',
        date: '',
        time: '',
        notes: ''
    });

    const [selectedMeeting, setSelectedMeeting] = useState(null);
    const [isEditingNotes, setIsEditingNotes] = useState(false);
    const [editedNotes, setEditedNotes] = useState('');
    const [isSaving, setIsSaving] = useState(false);

    const { userData } = useAuth();
    const { showConfirm } = useAlert();

    useEffect(() => {
        const fetchData = async () => {
            try {
                const [{ data: tData }, { data: mData }] = await Promise.all([
                    supabase.from('tenants').select('*'),
                    supabase.from('system_meetings').select('*').order('date', { ascending: true })
                ]);

                setTenants((tData || []).map(r => ({ ...r })));
                setMeetings((mData || []).map(r => ({
                    ...r,
                    tenantId: r.tenant_id,
                    customBusinessName: r.custom_business_name,
                    createdAt: r.created_at
                })));
            } catch (err) {
                console.error("Error fetching data for calendar:", err);
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, []);

    const handleAddMeeting = async (e) => {
        e.preventDefault();
        try {
            const { data, error } = await supabase
                .from('system_meetings')
                .insert({
                    tenant_id: newMeeting.tenantId,
                    custom_business_name: newMeeting.customBusinessName,
                    date: newMeeting.date,
                    time: newMeeting.time,
                    notes: newMeeting.notes,
                    created_at: new Date().toISOString()
                })
                .select('id')
                .single();
            if (error) throw error;
            setMeetings(prev => [...prev, { id: data.id, ...newMeeting }].sort((a, b) => a.date.localeCompare(b.date)));
            setMeetingModalOpen(false);
            setNewMeeting({ tenantId: '', customBusinessName: '', date: '', time: '', notes: '' });
        } catch (err) {
            console.error("Error adding meeting:", err);
        }
    };

    const handleDeleteMeeting = async (id) => {
        if (await showConfirm(t('common.confirmDelete'), { title: 'Görüşmeyi Sil', confirmText: 'Sil', type: 'warning' })) {
            try {
                const { error } = await supabase
                    .from('system_meetings')
                    .delete()
                    .eq('id', id);
                if (error) throw error;
                setMeetings(prev => prev.filter(m => m.id !== id));
                if (selectedMeeting?.id === id) setSelectedMeeting(null);
            } catch (err) {
                console.error("Error deleting meeting:", err);
            }
        }
    };

    const handleUpdateNotes = async () => {
        if (!selectedMeeting) return;
        setIsSaving(true);
        try {
            const { error } = await supabase
                .from('system_meetings')
                .update({ notes: editedNotes })
                .eq('id', selectedMeeting.id);
            if (error) throw error;
            setMeetings(prev => prev.map(m => m.id === selectedMeeting.id ? { ...m, notes: editedNotes } : m));
            setSelectedMeeting(prev => ({ ...prev, notes: editedNotes }));
            setIsEditingNotes(false);
        } catch (err) {
            console.error("Error updating notes:", err);
        } finally {
            setIsSaving(false);
        }
    };

    const handleMeetingClick = (m) => {
        setSelectedMeeting(m);
        setEditedNotes(m.notes || '');
        setIsEditingNotes(false);
    };

    // Calendar logic
    const daysInMonth = (year, month) => new Date(year, month + 1, 0).getDate();
    const firstDayOfMonth = (year, month) => new Date(year, month, 1).getDay();

    const renderCalendar = () => {
        const year = curDate.getFullYear();
        const month = curDate.getMonth();
        const days = [];
        const totalDays = daysInMonth(year, month);
        const startDay = firstDayOfMonth(year, month);

        // Blank days
        for (let i = 0; i < startDay; i++) {
            days.push(<div key={`blank-${i}`} style={{ height: '120px', border: '1px solid var(--border)', background: 'rgba(var(--primary-rgb), 0.01)' }}></div>);
        }

        // Month days
        for (let i = 1; i <= totalDays; i++) {
            const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(i).padStart(2, '0')}`;
            const dayMeetings = meetings.filter(m => m.date === dateStr);
            const isToday = new Date().toDateString() === new Date(year, month, i).toDateString();

            days.push(
                <div key={i} style={{
                    height: '140px',
                    border: '1px solid var(--border)',
                    padding: '8px',
                    background: isToday ? 'rgba(var(--primary-rgb), 0.05)' : 'var(--bg-card)',
                    overflowY: 'auto'
                }}>
                    <div style={{
                        fontWeight: 700,
                        fontSize: '0.875rem',
                        marginBottom: '8px',
                        color: isToday ? 'var(--primary)' : 'var(--text-main)',
                        display: 'flex',
                        justifyContent: 'space-between'
                    }}>
                        <span>{i}</span>
                        {isToday && <span style={{ fontSize: '0.625rem', textTransform: 'uppercase' }}>Bugün</span>}
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                        {dayMeetings.map(m => {
                            const tenant = tenants.find(t => t.id === m.tenantId);
                            return (
                                <div
                                    key={m.id}
                                    onClick={() => handleMeetingClick(m)}
                                    style={{
                                        fontSize: '0.625rem',
                                        padding: '4px 6px',
                                        borderRadius: '4px',
                                        background: 'var(--primary)',
                                        color: 'white',
                                        fontWeight: 600,
                                        cursor: 'pointer',
                                        whiteSpace: 'nowrap',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center'
                                    }}
                                >
                                    <span>{m.time} - {tenant?.name || m.customBusinessName || '...'}</span>
                                    <Trash2 size={10} onClick={(e) => { e.stopPropagation(); handleDeleteMeeting(m.id); }} />
                                </div>
                            );
                        })}
                    </div>
                </div>
            );
        }

        return days;
    };

    const nextMonth = () => setCurDate(new Date(curDate.getFullYear(), curDate.getMonth() + 1, 1));
    const prevMonth = () => setCurDate(new Date(curDate.getFullYear(), curDate.getMonth() - 1, 1));

    if (!userData?.isSuperAdmin) return <div className="p-8 text-center">{t('common.accessDenied')}</div>;

    const selectedTenant = tenants.find(t => t.id === selectedMeeting?.tenantId);

    return (
        <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
                <div>
                    <h1 style={{ fontSize: '2.25rem', fontWeight: 900, letterSpacing: '-0.02em', marginBottom: '0.5rem' }}>{t('calendar.title') || 'Görüşme Takvimi'}</h1>
                    <p style={{ color: 'var(--text-muted)', fontWeight: 600 }}>İşletme görüşmelerini ve randevularını yönetin.</p>
                </div>
                <button
                    onClick={() => setMeetingModalOpen(true)}
                    style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
                >
                    <Plus size={18} /> {t('dashboard.addMeeting')}
                </button>
            </div>

            <div className="card glass" style={{ padding: '1.5rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                        <h2 style={{ fontSize: '1.25rem', fontWeight: 700 }}>
                            {curDate.toLocaleString('default', { month: 'long', year: 'numeric' })}
                        </h2>
                    </div>
                    <div style={{ display: 'flex', gap: '8px' }}>
                        <button onClick={prevMonth} className="btn-icon"><ChevronLeft size={20} /></button>
                        <button onClick={() => setCurDate(new Date())} style={{ padding: '4px 12px', fontSize: '0.875rem' }}>Bugün</button>
                        <button onClick={nextMonth} className="btn-icon"><ChevronRight size={20} /></button>
                    </div>
                </div>

                <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(7, 1fr)',
                    gap: '1px',
                    background: 'var(--border)',
                    border: '1px solid var(--border)',
                    borderRadius: '12px',
                    overflow: 'hidden'
                }}>
                    {['Paz', 'Pzt', 'Sal', 'Çar', 'Per', 'Cum', 'Cmt'].map(day => (
                        <div key={day} style={{
                            padding: '12px',
                            textAlign: 'center',
                            background: 'rgba(var(--primary-rgb), 0.02)',
                            fontWeight: 700,
                            fontSize: '0.75rem',
                            color: 'var(--text-muted)',
                            textTransform: 'uppercase'
                        }}>{day}</div>
                    ))}
                    {renderCalendar()}
                </div>
            </div>

            {/* Meeting Detail Modal */}
            {selectedMeeting && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: 'rgba(0,0,0,0.4)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 1000,
                    backdropFilter: 'blur(8px)'
                }}>
                    <div className="card glass" style={{
                        width: '560px',
                        maxHeight: '85vh',
                        padding: '2rem',
                        border: '1px solid rgba(255,255,255,0.1)',
                        boxShadow: '0 25px 50px -12px rgba(0,0,0,0.5)',
                        display: 'flex',
                        flexDirection: 'column',
                        overflow: 'hidden'
                    }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem', flexShrink: 0 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                <div style={{ padding: '8px', background: 'var(--primary)20', color: 'var(--primary)', borderRadius: '8px' }}>
                                    <Calendar size={20} />
                                </div>
                                <h2 style={{ fontSize: '1.25rem', fontWeight: 800 }}>Görüşme Detayları</h2>
                            </div>
                            <button onClick={() => setSelectedMeeting(null)} style={{ background: 'transparent', color: 'var(--text-muted)', border: 'none', cursor: 'pointer' }}>
                                <X size={24} />
                            </button>
                        </div>

                        <div style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '1.5rem',
                            overflowY: 'auto',
                            paddingRight: '8px',
                            flex: 1
                        }} className="custom-scrollbar">
                            {/* Business Info Header */}
                            <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '16px',
                                padding: '1.25rem',
                                background: 'rgba(255,255,255,0.03)',
                                borderRadius: '16px',
                                border: '1px solid var(--border)',
                                flexShrink: 0
                            }}>
                                <div style={{
                                    width: '56px',
                                    height: '56px',
                                    borderRadius: '14px',
                                    background: '#6f4e3720',
                                    color: '#6f4e37',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    border: '1px solid rgba(255,255,255,0.05)',
                                    fontSize: '1.5rem'
                                }}>
                                    {selectedTenant?.theme?.logoUrl ? (
                                        <img src={selectedTenant.theme.logoUrl} style={{ width: '100%', height: '100%', objectFit: 'contain', padding: '10px' }} alt="" />
                                    ) : <Building2 size={28} />}
                                </div>
                                <div style={{ flex: 1 }}>
                                    <div style={{ fontWeight: 800, fontSize: '1.25rem', marginBottom: '2px' }}>
                                        {selectedTenant?.name || selectedMeeting.customBusinessName || 'Bilinmeyen İşletme'}
                                    </div>
                                    <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', fontFamily: 'monospace', letterSpacing: '0.5px' }}>
                                        {selectedMeeting.tenantId === 'custom' ? 'Dış İşletme' : selectedMeeting.tenantId}
                                    </div>
                                </div>
                            </div>

                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', flexShrink: 0 }}>
                                <div style={{ padding: '1rem', borderRadius: '16px', border: '1px solid var(--border)', background: 'rgba(255,255,255,0.02)' }}>
                                    <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                        <Calendar size={14} /> Tarih
                                    </div>
                                    <div style={{ fontWeight: 700, fontSize: '1rem' }}>{selectedMeeting.date}</div>
                                </div>
                                <div style={{ padding: '1rem', borderRadius: '16px', border: '1px solid var(--border)', background: 'rgba(255,255,255,0.02)' }}>
                                    <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                        <Clock size={14} /> Saat
                                    </div>
                                    <div style={{ fontWeight: 700, fontSize: '1rem' }}>{selectedMeeting.time}</div>
                                </div>
                            </div>

                            {/* Editable Notes Section */}
                            <div style={{
                                padding: '1.25rem',
                                borderRadius: '16px',
                                border: '1px solid var(--border)',
                                background: 'rgba(255,255,255,0.02)',
                                minHeight: '200px',
                                position: 'relative',
                                display: 'flex',
                                flexDirection: 'column'
                            }}>
                                <div style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    marginBottom: '12px',
                                    flexShrink: 0
                                }}>
                                    <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '6px' }}>
                                        <ScrollText size={14} /> Notlar
                                    </div>
                                    {!isEditingNotes ? (
                                        <button
                                            onClick={() => setIsEditingNotes(true)}
                                            style={{
                                                background: 'rgba(var(--primary-rgb), 0.1)',
                                                color: 'var(--primary)',
                                                fontSize: '0.75rem',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '4px',
                                                padding: '4px 10px',
                                                borderRadius: '8px',
                                                border: '1px solid rgba(var(--primary-rgb), 0.2)',
                                                fontWeight: 600
                                            }}
                                            className="hover-bg"
                                        >
                                            <Edit2 size={12} /> Düzenle
                                        </button>
                                    ) : (
                                        <div style={{ display: 'flex', gap: '8px' }}>
                                            <button
                                                onClick={() => setIsEditingNotes(false)}
                                                style={{ background: 'transparent', color: 'var(--text-muted)', fontSize: '0.75rem', fontWeight: 600 }}
                                            >
                                                İptal
                                            </button>
                                            <button
                                                onClick={handleUpdateNotes}
                                                disabled={isSaving}
                                                style={{
                                                    background: 'var(--primary)',
                                                    color: 'white',
                                                    fontSize: '0.75rem',
                                                    padding: '4px 12px',
                                                    borderRadius: '8px',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: '4px',
                                                    fontWeight: 600
                                                }}
                                            >
                                                {isSaving ? '...' : <><Save size={12} /> Kaydet</>}
                                            </button>
                                        </div>
                                    )}
                                </div>

                                <div style={{ flex: 1, overflow: 'visible' }}>
                                    {isEditingNotes ? (
                                        <textarea
                                            value={editedNotes}
                                            onChange={(e) => setEditedNotes(e.target.value)}
                                            style={{
                                                width: '100%',
                                                height: '250px',
                                                background: 'rgba(0,0,0,0.2)',
                                                border: '1px solid var(--border)',
                                                borderRadius: '12px',
                                                padding: '12px',
                                                color: 'var(--text-main)',
                                                fontSize: '0.9375rem',
                                                fontFamily: 'inherit',
                                                resize: 'vertical',
                                                outline: 'none',
                                                lineHeight: '1.6'
                                            }}
                                            autoFocus
                                            placeholder="Notlarınızı buraya yazın..."
                                        />
                                    ) : (
                                        <div style={{
                                            fontSize: '0.9375rem',
                                            lineHeight: '1.7',
                                            whiteSpace: 'pre-wrap',
                                            color: selectedMeeting.notes ? 'var(--text-main)' : 'var(--text-muted)',
                                            fontStyle: selectedMeeting.notes ? 'normal' : 'italic'
                                        }}>
                                            {selectedMeeting.notes || 'Herhangi bir not eklenmemiş.'}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        <div style={{ display: 'flex', gap: '1rem', marginTop: '1.5rem', flexShrink: 0 }}>
                            <button
                                onClick={() => handleDeleteMeeting(selectedMeeting.id)}
                                style={{
                                    padding: '12px',
                                    borderRadius: '12px',
                                    background: 'rgba(239, 68, 68, 0.1)',
                                    color: '#ef4444',
                                    border: '1px solid rgba(239, 68, 68, 0.2)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: '8px',
                                    flex: 1,
                                    fontWeight: 600
                                }}
                                className="hover-danger"
                            >
                                <Trash2 size={18} /> Sil
                            </button>
                            <button
                                onClick={() => setSelectedMeeting(null)}
                                style={{
                                    flex: 2,
                                    background: 'var(--primary)',
                                    color: 'white',
                                    fontWeight: 700,
                                    borderRadius: '12px',
                                    padding: '12px'
                                }}
                            >
                                Kapat
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Add Meeting Modal */}
            {isMeetingModalOpen && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: 'rgba(0,0,0,0.5)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 1000,
                    backdropFilter: 'blur(4px)'
                }}>
                    <div className="card glass" style={{ width: '400px', padding: '2rem' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
                            <h2 style={{ fontSize: '1.25rem', fontWeight: 800 }}>{t('dashboard.addMeeting')}</h2>
                            <button onClick={() => setMeetingModalOpen(false)} style={{ background: 'transparent', color: 'var(--text-muted)' }}>
                                <X size={24} />
                            </button>
                        </div>
                        <form onSubmit={handleAddMeeting} style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                            <div className="form-group">
                                <label>{t('dashboard.business')}</label>
                                <select
                                    required
                                    value={newMeeting.tenantId}
                                    onChange={e => {
                                        const val = e.target.value;
                                        setNewMeeting({ ...newMeeting, tenantId: val, customBusinessName: val === 'custom' ? '' : newMeeting.customBusinessName });
                                    }}
                                >
                                    <option value="">İşletme Seçin</option>
                                    <option value="custom" style={{ fontWeight: 700, color: 'var(--primary)' }}>+ Liste Dışı Yeni İşletme</option>
                                    <optgroup label="Kayıtlı İşletmeler">
                                        {tenants.map(t => (
                                            <option key={t.id} value={t.id}>{t.name}</option>
                                        ))}
                                    </optgroup>
                                </select>
                            </div>

                            {newMeeting.tenantId === 'custom' && (
                                <div className="form-group">
                                    <label>İşletme Adı</label>
                                    <input
                                        type="text"
                                        required
                                        placeholder="Görüşülecek işletmenin adını girin..."
                                        value={newMeeting.customBusinessName}
                                        onChange={e => setNewMeeting({ ...newMeeting, customBusinessName: e.target.value })}
                                    />
                                </div>
                            )}
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                                <div className="form-group">
                                    <label>{t('dashboard.date')}</label>
                                    <input
                                        type="date"
                                        required
                                        value={newMeeting.date}
                                        onChange={e => setNewMeeting({ ...newMeeting, date: e.target.value })}
                                    />
                                </div>
                                <div className="form-group">
                                    <label>{t('dashboard.time')}</label>
                                    <input
                                        type="time"
                                        required
                                        value={newMeeting.time}
                                        onChange={e => setNewMeeting({ ...newMeeting, time: e.target.value })}
                                    />
                                </div>
                            </div>
                            <div className="form-group">
                                <label>{t('dashboard.notes')}</label>
                                <textarea
                                    rows="3"
                                    value={newMeeting.notes}
                                    onChange={e => setNewMeeting({ ...newMeeting, notes: e.target.value })}
                                    placeholder="Görüşme notları..."
                                />
                            </div>
                            <button type="submit" style={{ width: '100%', marginTop: '1rem' }}>
                                {t('common.save')}
                            </button>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default CalendarPage;
